/**
 * Utility functions for color manipulation.
 */

/**
 * Converts a hex color to HSL.
 * @param {string} hex - Hex color string (e.g., "#3B82F6")
 * @returns {Object} HSL object { h, s, l }
 */
export const hexToHsl = (hex) => {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  let max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Converts HSL to hex color.
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color string
 */
export const hslToHex = (h, s, l) => {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
};

/**
 * Generates a secondary color based on a primary color.
 * "Less color" version: Lighter and less saturated.
 * @param {string} primaryHex - Hex color of the primary
 * @returns {string} Hex color of the secondary
 */
export const generateSecondaryColor = (primaryHex) => {
  if (!primaryHex || !primaryHex.startsWith('#') || primaryHex.length !== 7) return '#64748B';
  
  const hsl = hexToHsl(primaryHex);
  
  // Logic for "Secondary" (Less color):
  // 1. Keep the hue
  // 2. Reduce saturation (e.g., to 30% of original, but at least 15%)
  // 3. Increase lightness significantly (e.g., set to 92-95% for a soft background effect)
  //    OR just shift it towards a lighter shade.
  
  const newS = Math.max(15, hsl.s * 0.4); 
  const newL = Math.max(85, Math.min(96, hsl.l + 40)); // Aim for a very light background shade
  
  return hslToHex(hsl.h, newS, newL);
};

/**
 * Adjusts the lightness of a hex color.
 * @param {string} hex - Hex color string
 * @param {number} amount - Amount to add/subtract from lightness (-100 to 100)
 * @returns {string} Hex color string
 */
export const adjustLightness = (hex, amount) => {
  if (!hex || !hex.startsWith('#') || hex.length !== 7) return hex;
  const hsl = hexToHsl(hex);
  const newL = Math.max(0, Math.min(100, hsl.l + amount));
  return hslToHex(hsl.h, hsl.s, newL);
};

/**
 * Generates a secondary color based on a primary color with an optional lightness offset.
 * @param {string} primaryHex - Hex color of the primary
 * @param {number} offset - Lightness offset to apply (-50 to 50)
 * @returns {string} Hex color of the secondary
 */
export const generateSecondaryColorWithOffset = (primaryHex, offset = 0) => {
  if (!primaryHex || !primaryHex.startsWith('#') || primaryHex.length !== 7) return '#64748B';
  const hsl = hexToHsl(primaryHex);
  const newS = Math.max(15, hsl.s * 0.4); 
  const newL = Math.max(10, Math.min(98, hsl.l + 40 + offset)); 
  return hslToHex(hsl.h, newS, newL);
};
